import {
  BarChart3,
  Briefcase,
  CreditCard,
  LayoutDashboard,
  LifeBuoy,
  Radar,
  Settings,
  UserCog,
  Users,
  type LucideIcon,
} from 'lucide-react';

export type NavLeaf = { title: string; url: string };

export type NavItem = {
  title: string;
  url: string;
  icon: LucideIcon;
  items?: NavLeaf[];
};

export type NavSection = {
  label: string;
  items: NavItem[];
};

/** Single source of truth for the sidebar nav and the ⌘K command palette. */
export const NAV_SECTIONS: NavSection[] = [
  {
    label: 'Overview',
    items: [
      { title: 'Dashboard', url: '/admin/dashboard', icon: LayoutDashboard },
      { title: 'Analytics', url: '/admin/analytics', icon: BarChart3 },
    ],
  },
  {
    label: 'Operations',
    items: [
      {
        title: 'Jobs',
        url: '/admin/jobs',
        icon: Briefcase,
        items: [
          { title: 'All Jobs', url: '/admin/jobs' },
          { title: 'Scraping Queue', url: '/admin/jobs/scraping-queue' },
          { title: 'Duplicates', url: '/admin/jobs/duplicates' },
        ],
      },
      {
        // Sibling of Jobs rather than a child of it: scout rows are a separate
        // pipeline awaiting review, and only reach Jobs once promoted.
        title: 'Scout',
        url: '/admin/scout/postings',
        icon: Radar,
        items: [
          { title: 'Postings', url: '/admin/scout/postings' },
          { title: 'Runs', url: '/admin/scout/runs' },
          { title: 'Targets', url: '/admin/scout/targets' },
          { title: 'Programs', url: '/admin/scout/programs' },
        ],
      },
      {
        // Sits under Operations, not Business: support is queue work that gets
        // drained daily, which is what everything else in this group is.
        title: 'Support',
        url: '/admin/support',
        icon: LifeBuoy,
      },
      {
        title: 'Employees',
        url: '/admin/employees',
        icon: Users,
        items: [
          { title: 'All Employees', url: '/admin/employees' },
          { title: 'Performance', url: '/admin/performance' },
        ],
      },
    ],
  },
  {
    label: 'Business',
    items: [
      {
        title: 'Customers',
        url: '/admin/customers',
        icon: UserCog,
        items: [
          { title: 'All Customers', url: '/admin/customers' },
          { title: 'Job Recommendations', url: '/admin/customers/recommendations' },
        ],
      },
      { title: 'Plans', url: '/admin/plans', icon: CreditCard },
    ],
  },
  {
    label: 'System',
    items: [{ title: 'Settings', url: '/admin/settings', icon: Settings }],
  },
];

/**
 * How strongly `path` matches `url`: exact match beats a prefix match, and a
 * longer prefix beats a shorter one. 0 = no match. Used to light up the single
 * most-specific link for the current route.
 */
export function matchScore(url: string, path: string): number {
  if (path === url) return url.length + 1;
  const prefix = url.endsWith('/') ? url : `${url}/`;
  return path.startsWith(prefix) ? url.length : 0;
}
